import { blockRegistry } from "@/lib/email-builder/block-registry";
import { TextBlock } from "./TextBlock";
import { HeadingBlock } from "./HeadingBlock";
import { ImageBlock } from "./ImageBlock";
import { ButtonBlock } from "./ButtonBlock";
import { SectionBlock } from "./SectionBlock";
import { ColumnBlock } from "./ColumnBlock";
import { DividerBlock } from "./DividerBlock";
import { SpacerBlock } from "./SpacerBlock";
import { HeroBlock } from "./HeroBlock";
import { FooterBlock } from "./FooterBlock";
import { SocialBlock } from "./SocialBlock";
import { VideoBlock } from "./VideoBlock";
import { QuoteBlock } from "./QuoteBlock";
import { MenuBlock } from "./MenuBlock";
import { HtmlBlock } from "./HtmlBlock";
import { GroupBlock } from "./GroupBlock";
import {
  createDefaultTextBlockConfiguration,
  createDefaultHeadingBlockConfiguration,
  createDefaultImageBlockConfiguration,
  createDefaultButtonBlockConfiguration,
  createDefaultSectionBlockConfiguration,
  createDefaultColumnBlockConfiguration,
  createDefaultDividerBlockConfiguration,
  createDefaultSpacerBlockConfiguration,
  createDefaultHeroBlockConfiguration,
  createDefaultFooterBlockConfiguration,
  createDefaultSocialBlockConfiguration,
  createDefaultVideoBlockConfiguration,
  createDefaultQuoteBlockConfiguration,
  createDefaultMenuBlockConfiguration,
  createDefaultHtmlBlockConfiguration,
  createDefaultGroupBlockConfiguration,
} from "@/types/block-configurations";

// Register parent/container blocks first to avoid unknown parent warnings

// Register Section Block (Container)
blockRegistry.register({
  type: "section",
  displayName: "Section",
  category: "structure",
  icon: "LayoutGrid",
  description: "Container for organizing columns and content",
  component: SectionBlock,
  defaultConfig: createDefaultSectionBlockConfiguration(),
  allowChildren: true,
  allowedParents: [], // Can be root level
  allowedChildren: ["column", "group"], // Content blocks go inside Columns; hero/footer are root-level
  sortOrder: 5,
});

// Register Column Block (Container)
blockRegistry.register({
  type: "column",
  displayName: "Column",
  category: "layout",
  icon: "Columns",
  description: "Vertical column for content",
  component: ColumnBlock,
  defaultConfig: createDefaultColumnBlockConfiguration(),
  allowChildren: true,
  allowedParents: ["section", "group"], // Can be in sections or groups
  allowedChildren: ["text", "heading", "button", "image", "divider", "spacer", "video", "social", "quote", "menu", "html"], // Content blocks only
  sortOrder: 15,
});

// Now register child blocks that reference section/column as parents

// Register Text Block
blockRegistry.register({
  type: "text",
  displayName: "Text",
  category: "content",
  icon: "Type",
  description: "Rich text content with formatting options",
  component: TextBlock,
  defaultConfig: createDefaultTextBlockConfiguration(),
  allowChildren: false,
  allowedParents: ["column", ""], // "" = document root (auto-wrapped in section+column on render)
  sortOrder: 10,
});

// Register Heading Block
blockRegistry.register({
  type: "heading",
  displayName: "Heading",
  category: "content",
  icon: "Heading",
  description: "Heading text with customizable level (H1-H6)",
  component: HeadingBlock,
  defaultConfig: createDefaultHeadingBlockConfiguration(),
  allowChildren: false,
  allowedParents: ["column", ""], // "" = document root (auto-wrapped in section+column on render)
  sortOrder: 15,
});

// Register Image Block
blockRegistry.register({
  type: "image",
  displayName: "Image",
  category: "media",
  icon: "Image",
  description: "Display images with customizable size and alignment",
  component: ImageBlock,
  defaultConfig: createDefaultImageBlockConfiguration(),
  allowChildren: false,
  allowedParents: ["column", ""], // "" = document root (auto-wrapped in section+column on render)
  sortOrder: 20,
});

// Register Button Block
blockRegistry.register({
  type: "button",
  displayName: "Button",
  category: "content",
  icon: "MousePointerClick",
  description: "Call-to-action button with link",
  component: ButtonBlock,
  defaultConfig: createDefaultButtonBlockConfiguration(),
  allowChildren: false,
  allowedParents: ["column", ""], // "" = document root (auto-wrapped in section+column on render)
  sortOrder: 30,
});

// Register Divider Block
blockRegistry.register({
  type: "divider",
  displayName: "Divider",
  category: "content",
  icon: "Minus",
  description: "Horizontal line to separate content",
  component: DividerBlock,
  defaultConfig: createDefaultDividerBlockConfiguration(),
  allowChildren: false,
  allowedParents: ["column", ""], // "" = document root (auto-wrapped in section+column on render)
  sortOrder: 40,
});

// Register Spacer Block
blockRegistry.register({
  type: "spacer",
  displayName: "Spacer",
  category: "layout",
  icon: "Space",
  description: "Add vertical spacing between blocks",
  component: SpacerBlock,
  defaultConfig: createDefaultSpacerBlockConfiguration(),
  allowChildren: false,
  allowedParents: ["column", ""], // "" = document root (auto-wrapped in section+column on render)
  sortOrder: 50,
});

// Register Group Block — mirrors MJML's mj-group: holds columns that stay
// side by side on mobile instead of stacking
blockRegistry.register({
  type: "group",
  displayName: "Group",
  category: "layout",
  icon: "Group",
  description: "Holds columns side by side that don't stack on mobile",
  component: GroupBlock,
  defaultConfig: createDefaultGroupBlockConfiguration(),
  allowChildren: true,
  allowedParents: ["section"], // mj-group is only valid directly inside a section
  allowedChildren: ["column"], // backend drops anything else from groups
  sortOrder: 20,
});

// Register Hero Block
blockRegistry.register({
  type: "hero",
  displayName: "Hero",
  category: "layout",
  icon: "Star",
  description: "Full-width hero section with title, subtitle, and CTA",
  component: HeroBlock,
  defaultConfig: createDefaultHeroBlockConfiguration(),
  allowChildren: false,
  allowedParents: [""], // "" = document root only
  sortOrder: 1,
});

// Register Footer Block
blockRegistry.register({
  type: "footer",
  displayName: "Footer",
  category: "layout",
  icon: "Footer",
  description: "Email footer with company info, links, social icons, and unsubscribe",
  component: FooterBlock,
  defaultConfig: createDefaultFooterBlockConfiguration(),
  allowChildren: false,
  allowedParents: [""], // "" = document root only
  sortOrder: 2,
});

// Register Social Block
blockRegistry.register({
  type: "social",
  displayName: "Social",
  category: "media",
  icon: "Share2",
  description: "Social media icons with links",
  component: SocialBlock,
  defaultConfig: createDefaultSocialBlockConfiguration(),
  allowChildren: false,
  allowedParents: ["column", ""],
  sortOrder: 25,
});

// Register Video Block
blockRegistry.register({
  type: "video",
  displayName: "Video",
  category: "media",
  icon: "Play",
  description: "Video with thumbnail fallback for email clients",
  component: VideoBlock,
  defaultConfig: createDefaultVideoBlockConfiguration(),
  allowChildren: false,
  allowedParents: ["column", ""],
  sortOrder: 30,
});

// Register Quote Block
blockRegistry.register({
  type: "quote",
  displayName: "Quote",
  category: "content",
  icon: "Quote",
  description: "Styled blockquote with optional author and source",
  component: QuoteBlock,
  defaultConfig: createDefaultQuoteBlockConfiguration(),
  allowChildren: false,
  allowedParents: ["column", ""],
  sortOrder: 35,
});

// Register Menu Block
blockRegistry.register({
  type: "menu",
  displayName: "Menu",
  category: "content",
  icon: "Menu",
  description: "Navigation menu with horizontal or vertical layout",
  component: MenuBlock,
  defaultConfig: createDefaultMenuBlockConfiguration(),
  allowChildren: false,
  allowedParents: ["column", ""],
  sortOrder: 45,
});

// Register HTML Block
blockRegistry.register({
  type: "html",
  displayName: "HTML",
  category: "content",
  icon: "Code",
  description: "Raw HTML content for advanced customization",
  component: HtmlBlock,
  defaultConfig: createDefaultHtmlBlockConfiguration(),
  allowChildren: false,
  allowedParents: ["column", ""],
  sortOrder: 55,
});

// Export all for convenience
export * from "./TextBlock";
export * from "./ImageBlock";
export * from "./ButtonBlock";
export * from "./SectionBlock";
export * from "./ColumnBlock";
export * from "./DividerBlock";
export * from "./SpacerBlock";
export * from "./HeroBlock";
export * from "./FooterBlock";
export * from "./SocialBlock";
export * from "./VideoBlock";
export * from "./QuoteBlock";
export * from "./MenuBlock";
export * from "./HtmlBlock";
export * from "./GroupBlock";
